// Activity calendar body — GitHub-style heatmap of "checkins with this
// feature, per day", three months wide with prev/next navigation.
//
// Mirrors ProjectActivityCalendar's look (cell size, intensity ramp,
// legend, nav controls) so a feature's activity reads identically to its
// project's. Anchored at the latest data point on first load — for a
// sparse or stale feature, today→today-2mo is empty, but the user almost
// always wants to see when activity actually happened.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import ActivityCalendar, { HexCell, intensityClassFor } from '../../components/ActivityCalendar';
import type { ChartBodyProps } from '../types';

interface ValueRow {
  date: string;            // ISO yyyy-mm-dd
  decimal_value: number | null;
  freeform_value: string | null;
  reference_value: number | null;
}

const MONTHS_TO_SHOW = 3;
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Unlike every other body, ActivityCalendarBody doesn't render a
// ChartTimeRangePicker — it already has its own 3-month window with
// prev/next/today nav, and overlaying a second time selector would be
// confusing.
const ActivityCalendarBody: React.FC<ChartBodyProps> = ({ featureId, fullscreen }) => {
  const [rows, setRows] = useState<ValueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Anchor lazily — set after fetch so we can land on the latest data point.
  const today = new Date();
  const [endYear, setEndYear] = useState(today.getFullYear());
  const [endMonth, setEndMonth] = useState(today.getMonth());
  const [anchorPicked, setAnchorPicked] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setAnchorPicked(false);
    api.get<ValueRow[]>(`/api/features/values/${featureId}`)
      .then((resp) => {
        if (cancelled) return;
        setRows(resp.data);
        setError(null);
      })
      .catch((err) => {
        console.error('Failed to fetch feature values:', err);
        if (!cancelled) setError('Failed to load activity data');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [featureId]);

  // After data loads, anchor the visible window at the latest data point
  // (or today if more recent / no data).
  useEffect(() => {
    if (loading || anchorPicked) return;
    let latest: Date | null = null;
    for (const r of rows) {
      const d = new Date(r.date.slice(0, 10) + 'T00:00:00');
      if (!latest || d > latest) latest = d;
    }
    const now = new Date();
    const target = !latest || latest > now ? now : latest;
    setEndYear(target.getFullYear());
    setEndMonth(target.getMonth());
    setAnchorPicked(true);
  }, [loading, rows, anchorPicked]);

  const activityData = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of rows) {
      const day = r.date.slice(0, 10);
      counts[day] = (counts[day] ?? 0) + 1;
    }
    return counts;
  }, [rows]);

  // Pin the right edge into view whenever the visible window changes — most
  // recent month is the one the user wants to land on. useLayoutEffect so
  // the scroll happens before paint (no flash of left-aligned content).
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollLeft = el.scrollWidth;
  }, [endYear, endMonth, rows.length]);

  const monthsToDisplay = useMemo(() => {
    const out: { month: number; year: number }[] = [];
    for (let i = MONTHS_TO_SHOW - 1; i >= 0; i--) {
      let m = endMonth - i;
      let y = endYear;
      while (m < 0) { m += 12; y -= 1; }
      out.push({ month: m, year: y });
    }
    return out;
  }, [endMonth, endYear]);

  const totalInWindow = useMemo(() => {
    let total = 0;
    for (const { month, year } of monthsToDisplay) {
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      for (let d = 1; d <= daysInMonth; d++) {
        const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        total += activityData[iso] ?? 0;
      }
    }
    return total;
  }, [monthsToDisplay, activityData]);

  // Scale colors against the max in the visible window — keeps the
  // contrast useful even when the user navigates to a quiet stretch.
  const maxActivity = useMemo(() => {
    let max = 1;
    for (const { month, year } of monthsToDisplay) {
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      for (let d = 1; d <= daysInMonth; d++) {
        const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        const c = activityData[iso] ?? 0;
        if (c > max) max = c;
      }
    }
    return max;
  }, [monthsToDisplay, activityData]);

  const goPrevious = () => {
    let m = endMonth - MONTHS_TO_SHOW;
    let y = endYear;
    while (m < 0) { m += 12; y -= 1; }
    setEndMonth(m); setEndYear(y);
  };
  const goNext = () => {
    let m = endMonth + MONTHS_TO_SHOW;
    let y = endYear;
    while (m > 11) { m -= 12; y += 1; }
    const now = new Date();
    if (y > now.getFullYear() || (y === now.getFullYear() && m > now.getMonth())) {
      setEndYear(now.getFullYear());
      setEndMonth(now.getMonth());
    } else {
      setEndMonth(m); setEndYear(y);
    }
  };
  const goToday = () => {
    const now = new Date();
    setEndYear(now.getFullYear());
    setEndMonth(now.getMonth());
  };
  const isCurrentPeriod = endYear === today.getFullYear() && endMonth === today.getMonth();

  const renderCell = (date: string, activityCount: number) => {
    const dateObj = new Date(date + 'T00:00:00');
    const formatted = dateObj.toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
    return (
      <HexCell
        colorClass={intensityClassFor(activityCount, maxActivity)}
        title={`${formatted}: ${activityCount} checkin${activityCount === 1 ? '' : 's'}`}
      />
    );
  };

  if (loading) {
    return (
      <div className={`flex justify-center items-center ${fullscreen ? 'h-full' : 'h-64'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600" />
      </div>
    );
  }
  if (error) {
    return <div className="text-center py-8 text-rose-500"><p>{error}</p></div>;
  }
  if (rows.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No checkins recorded yet for this feature.</p>
      </div>
    );
  }

  return (
    <div className={fullscreen ? 'h-full overflow-auto px-2 py-4' : 'px-2 py-2'}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button
            onClick={goPrevious}
            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
            title="Previous months"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={goNext}
            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
            title="Next months"
            disabled={isCurrentPeriod}
          >
            <svg className={`w-4 h-4 ${isCurrentPeriod ? 'text-gray-300' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          {!isCurrentPeriod && (
            <button
              onClick={goToday}
              className="text-sm text-amber-800 hover:text-amber-900 underline ml-1"
              title="Go to current month"
            >
              Today
            </button>
          )}
        </div>
        <div className="text-xs text-gray-500">
          {totalInWindow} checkin{totalInWindow === 1 ? '' : 's'} in view
        </div>
      </div>

      <div ref={scrollRef} className="flex gap-4 overflow-x-auto">
        {monthsToDisplay.map(({ month, year }) => (
          <div key={`${year}-${month}`} className="flex-shrink-0">
            <div className="text-[10px] font-medium text-gray-500 mb-1 text-center">
              {MONTH_NAMES[month].slice(0, 3)} {year !== today.getFullYear() ? `'${String(year).slice(-2)}` : ''}
            </div>
            <ActivityCalendar
              year={year}
              month={month}
              activityData={activityData}
              renderCell={renderCell}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActivityCalendarBody;
